/**
 * Authentication utility functions for the Thaklis application
 */
import axios from 'axios';

// Constants for storage keys
const STORAGE_KEYS = {
  TOKEN: 'token',
  JWT_TOKEN: 'jwt_token',
  REFRESH_TOKEN: 'refresh_token',
  AUTH_INFO: 'authInfo',
  AUTH_TOKEN: 'authToken',
  CURRENT_USER: 'currentUser',
  ORGANIZATION_CODE: 'organizationCode',
  USER_ID: 'userId',
  USER_EMAIL: 'userEmail',
  USER_ROLES: 'userRoles'
};

/**
 * Completely resets all authentication and user data
 * Call this before login or when switching users
 */
export const resetAllAuthData = () => {
  console.log('Resetting all authentication and user data');
  
  // Clear all token storage locations
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Completely remove authInfo object instead of just clearing values
  localStorage.removeItem(STORAGE_KEYS.AUTH_INFO);
  
  // Clear session storage as well
  sessionStorage.clear();
  
  // Clear axios default Authorization header
  if (axios.defaults.headers.common['Authorization']) {
    delete axios.defaults.headers.common['Authorization'];
    console.log('Cleared axios Authorization header');
  }
  
  console.log('All authentication data has been reset');
};

/**
 * Retrieves the authentication token from local storage
 * @returns {string|null} The authentication token or null if not found
 */
export const getAuthToken = () => {
  // Try multiple storage locations
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || 
                localStorage.getItem(STORAGE_KEYS.JWT_TOKEN) || 
                localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  
  if (token) return token;
  
  // If not found in direct storage, try authInfo
  try {
    const authInfo = localStorage.getItem(STORAGE_KEYS.AUTH_INFO);
    if (authInfo) {
      const authInfoObj = JSON.parse(authInfo);
      if (authInfoObj.token) {
        // Save it in our preferred format for future use
        localStorage.setItem(STORAGE_KEYS.TOKEN, authInfoObj.token);
        return authInfoObj.token;
      }
    }
  } catch (error) {
    console.error('Error retrieving token from authInfo:', error);
  }
  
  return null;
};

/**
 * Saves the authentication token to local storage
 * @param {string} token - The authentication token to save
 */
export const setAuthToken = (token) => {
  if (!token) {
    console.warn('Attempted to save null or empty token');
    return;
  }
  
  // Save in all storage locations for compatibility
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
  localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  
  // Also save in authInfo format
  try {
    let authInfo = localStorage.getItem(STORAGE_KEYS.AUTH_INFO);
    let authInfoObj = authInfo ? JSON.parse(authInfo) : {};
    authInfoObj.token = token;
    localStorage.setItem(STORAGE_KEYS.AUTH_INFO, JSON.stringify(authInfoObj));
  } catch (error) {
    console.error('Error saving token in authInfo format:', error);
  }
  
  // Configure axios default headers
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

/**
 * Removes the authentication token from local storage
 */
export const removeAuthToken = () => {
  // Remove from all storage locations
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.JWT_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  
  // Also remove from authInfo
  try {
    let authInfo = localStorage.getItem(STORAGE_KEYS.AUTH_INFO);
    if (authInfo) {
      let authInfoObj = JSON.parse(authInfo);
      delete authInfoObj.token;
      localStorage.setItem(STORAGE_KEYS.AUTH_INFO, JSON.stringify(authInfoObj));
    }
  } catch (error) {
    console.error('Error removing token from authInfo:', error);
  }
  
  // Remove from axios headers
  delete axios.defaults.headers.common['Authorization'];
};

/**
 * Saves the organization code to local storage
 * @param {string} code - The organization code to save
 */
export const setOrganizationCode = (code) => {
  if (code) {
    localStorage.setItem(STORAGE_KEYS.ORGANIZATION_CODE, code);
  }
};

/**
 * Gets the organization code from local storage
 * @returns {string|null} The organization code or null if not found
 */
export const getOrganizationCode = () => {
  return localStorage.getItem(STORAGE_KEYS.ORGANIZATION_CODE);
};

/**
 * Checks if the user is authenticated
 * @returns {boolean} True if the user is authenticated, false otherwise
 */
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

/**
 * Adds the authentication token to the headers of an API request
 * @param {Object} headers - The headers object to add the token to
 * @returns {Object} The headers object with the token added
 */
export const addAuthHeader = (headers = {}) => {
  const token = getAuthToken();
  if (token) {
    return {
      ...headers,
      'Authorization': `Bearer ${token}`
    };
  }
  return headers;
};

/**
 * Setup axios interceptors to handle authentication
 */
export const setupAuthInterceptors = () => {
  // Request interceptor to add auth token to all requests
  axios.interceptors.request.use(
    config => {
      const token = getAuthToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );
  
  // Response interceptor to handle authentication errors
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.response && error.response.status === 401) {
        console.warn('Authentication error detected, clearing tokens');
        resetAllAuthData();
        // Redirect to login page
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Initialize auth interceptors when this module is imported
setupAuthInterceptors();
